import React, { useEffect, useState, createContext, useContext } from 'react';
// Define types for our currency context
type Currency = {
  code: string;
  symbol: string;
  rate: number; // Exchange rate from ARS
};
type CurrencyContextType = {
  currency: Currency;
  isLoading: boolean;
  error: string | null;
};
// Default currency (Argentine Peso)
const defaultCurrency: Currency = {
  code: 'ARS',
  symbol: 'ARS',
  rate: 1
};
// Common currencies and their approximate exchange rates from ARS
// These rates would need to be updated regularly in a production app
const currencyMap: Record<string, Currency> = {
  // Default currency
  AR: {
    code: 'ARS',
    symbol: 'ARS',
    rate: 1
  },
  // Americas
  US: {
    code: 'USD',
    symbol: '$',
    rate: 0.000753 // Updated: 11,955 ARS = $9 USD
  },
  CA: {
    code: 'CAD',
    symbol: 'C$',
    rate: 0.00093 // Kept as is
  },
  MX: {
    code: 'MXN',
    symbol: 'MX$',
    rate: 0.0154 // Updated: 11,955 ARS = 184.44 MXN
  },
  BR: {
    code: 'BRL',
    symbol: 'R$',
    rate: 0.0035 // Kept as is
  },
  CL: {
    code: 'CLP',
    symbol: 'CLP',
    rate: 0.8154 // Updated: 11,955 ARS = 9,748 CLP
  },
  UY: {
    code: 'UYU',
    symbol: '$U',
    rate: 0.0288 // Updated: 11,955 ARS = 344 UYU
  },
  CO: {
    code: 'COP',
    symbol: 'COP',
    rate: 2.7969 // Updated: 11,955 ARS = 33,437 COP
  },
  // Europe
  GB: {
    code: 'GBP',
    symbol: '£',
    rate: 0.00054 // Kept as is
  },
  EU: {
    code: 'EUR',
    symbol: '€',
    rate: 0.00064 // Kept as is
  },
  // Asia/Pacific
  AU: {
    code: 'AUD',
    symbol: 'A$',
    rate: 0.00103 // Kept as is
  },
  JP: {
    code: 'JPY',
    symbol: '¥',
    rate: 0.103 // Kept as is
  },
  CN: {
    code: 'CNY',
    symbol: '¥',
    rate: 0.0049 // Kept as is
  },
  // Default for any other country
  DEFAULT: {
    code: 'USD',
    symbol: '$',
    rate: 0.000753 // Updated to match US rate
  }
};
// Create the context
const CurrencyContext = createContext<CurrencyContextType>({
  currency: defaultCurrency,
  isLoading: true,
  error: null
});
export const useCurrency = () => useContext(CurrencyContext);
export const CurrencyProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [currency, setCurrency] = useState<Currency>(defaultCurrency);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const detectUserCurrency = async () => {
      try {
        // Fetch user's country from IP
        const response = await fetch('https://ipapi.co/json/');
        if (!response.ok) {
          throw new Error('Failed to fetch location data');
        }
        const data = await response.json();
        const countryCode = data.country_code;
        // Set currency based on country code
        setCurrency(currencyMap[countryCode] || currencyMap.DEFAULT);
      } catch (err) {
        console.error('Error detecting currency:', err);
        setError('Could not detect your location. Showing default currency.');
        setCurrency(currencyMap.DEFAULT);
      } finally {
        setIsLoading(false);
      }
    };
    detectUserCurrency();
  }, []);
  return <CurrencyContext.Provider value={{
    currency,
    isLoading,
    error
  }} data-id="element-639">
      {children}
    </CurrencyContext.Provider>;
};